// import AccreditIcon from '@/components/accreditDia/AccreditIcon'
import { AssistantIcon } from '@/components/bs-icons/assistant'
import { cname } from '@/components/bs-ui/utils'
import { Func } from '@/permissions/constants'
import { useFuncsStore } from '@/store/funcStore'
import { useBuriedEvent } from '@/util/buried'
import { Tooltip } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { GoIcon } from '../../bs-icons/go'
import { PlusIcon } from '../../bs-icons/plus'
import { SkillIcon } from '../../bs-icons/skill'
// import { UserIcon } from '../../bs-icons/user'
import { SmallDashOutlined } from '@ant-design/icons'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../bs-ui/card'
import { Switch } from '../../bs-ui/switch'
import './styles.css'

/**
 * 卡片组件属性接口
 *
 * 定义了卡片组件的所有可配置属性，支持多种卡片类型和交互功能
 *
 * @template T - 数据类型，用于泛型约束
 */
interface IProps<T> {
  /** 卡片关联的数据对象 */
  data: any
  /** 卡片唯一标识符，为空字符串时表示新建卡片 */
  id?: number | string
  /** 卡片类型：技能卡片、侧边弹窗列表、助手卡片、设置卡片 */
  type: 'skill' | 'sheet' | 'assist' | 'setting'
  /** 卡片标题 */
  title: string
  /** 是否可编辑模式 */
  edit?: boolean
  /** 卡片描述内容，支持字符串或React节点 */
  description: React.ReactNode | string
  /** 开关状态（在线/离线） */
  checked?: boolean
  /** 创建者用户名 */
  user?: string
  /** 是否为管理员 */
  isAdmin?: boolean
  /** 头部选择器组件 */
  headSelecter?: React.ReactNode
  /** 底部内容组件 */
  footer?: React.ReactNode
  /** 自定义图标组件 */
  icon?: any
  /** 卡片Logo图片URL */
  logo?: string
  /** 卡片点击事件处理函数 */
  onClick?: () => void
  /** 开关点击事件处理函数 */
  onSwitchClick?: () => void
  /** 添加到模板事件处理函数 */
  onAddTemp?: (data: T) => void
  /** 开关状态变化事件处理函数 */
  onCheckedChange?: (b: boolean, data: T) => Promise<any>
  /** 删除事件处理函数 */
  onDelete?: (data: T) => void
  /** 设置事件处理函数 */
  onSetting?: (data: any) => void
  /** 授权弹窗事件处理函数 */
  onAccreditDia?: () => void
  /** 数据信息事件处理函数 */
  onDataInfo?: () => void
}

/** 预定义的渐变色背景数组，用于为卡片图标生成不同的背景色 */
export const gradients = ['bg-amber-500', 'bg-orange-600', 'bg-teal-500', 'bg-purple-600', 'bg-blue-700']

// 其他可用的渐变色选项（已注释）
// 'bg-slate-600',
// 'bg-amber-500',
// 'bg-red-600',
// 'bg-orange-600',
// 'bg-teal-500',
// 'bg-purple-600',
// 'bg-blue-700',
// 'bg-yellow-600',
// 'bg-emerald-600',
// 'bg-green-700',
// 'bg-cyan-600',
// 'bg-sky-600',
// 'bg-indigo-600',
// 'bg-violet-600',
// 'bg-purple-600',
// 'bg-fuchsia-700',
// 'bg-pink-600',
// 'bg-rose-600'

/**
 * 标题图标背景组件
 *
 * 为卡片图标提供动态生成的渐变色背景，根据ID自动选择颜色
 *
 * @param id - 用于生成背景色的唯一标识符
 * @param className - 额外的CSS类名
 * @param children - 子组件，默认为SkillIcon
 *
 * 使用场景：
 * 1. 技能卡片图标背景 - 为技能图标提供彩色背景
 * 2. 助手卡片图标背景 - 为助手图标提供彩色背景
 * 3. 应用卡片图标背景 - 为应用图标提供彩色背景
 * 4. 模板卡片图标背景 - 为模板图标提供彩色背景
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * <TitleIconBg id="123">
 *   <SkillIcon />
 * </TitleIconBg>
 *
 * // 自定义样式
 * <TitleIconBg id="456" className="w-8 h-8">
 *   <CustomIcon />
 * </TitleIconBg>
 *
 * // 使用默认图标
 * <TitleIconBg id="789" />
 * ```
 */
export function TitleIconBg({ id, className = '', children = <SkillIcon /> }) {
  return (
    <div
      className={cname(
        // 根据ID生成十六进制数，取模运算选择渐变色，确保颜色分布均匀
        `flex items-center justify-center rounded-sm ${gradients[parseInt(id + '', 16) % gradients.length]}`,
        className
      )}
    >
      {children}
    </div>
  )
}

/**
 * 通用卡片组件
 *
 * 这是一个高度可配置的卡片组件，支持多种卡片类型和丰富的交互功能。
 * 包括技能卡片、助手卡片、侧边弹窗列表卡片等，支持在线/离线状态切换、
 * 编辑、删除、数据查看等操作。
 *
 * @template T - 数据类型，用于泛型约束
 * @param props - 卡片组件的属性配置
 * @param _ref - React ref引用（当前未使用）
 *
 * 使用场景：
 * 1. 技能管理 - 技能列表中的技能卡片展示
 * 2. 助手管理 - 助手列表中的助手卡片展示
 * 3. 应用管理 - 应用列表中的应用卡片展示
 * 4. 模板管理 - 模板列表中的模板卡片展示
 * 5. 侧边弹窗 - 侧边栏中的卡片列表展示
 * 6. 新建卡片 - 用于创建新项目的空白卡片
 * 7. 数据看板 - 数据统计卡片展示
 * 8. 设置页面 - 设置选项卡片展示
 *
 * 使用示例：
 * ```tsx
 * // 技能卡片
 * <CardComponent
 *   id="skill-123"
 *   type="skill"
 *   title="智能客服"
 *   description="基于AI的智能客服助手"
 *   checked={true}
 *   user="张三"
 *   edit={true}
 *   onCheckedChange={handleStatusChange}
 *   onDelete={handleDelete}
 *   onSetting={handleEdit}
 * />
 *
 * // 新建卡片
 * <CardComponent
 *   id=""
 *   type="skill"
 *   title="创建新技能"
 *   description="点击创建新的AI技能"
 *   onClick={handleCreate}
 * />
 *
 * // 侧边弹窗卡片
 * <CardComponent
 *   type="sheet"
 *   title="模板选择"
 *   description="选择一个模板开始创建"
 *   footer={<Button>选择</Button>}
 *   onClick={handleSelect}
 * />
 * ```
 */
function CardComponent<T>(
  {
    id = '',
    data,
    type,
    icon: Icon = SkillIcon,
    logo,
    edit = false,
    user,
    title,
    checked,
    isAdmin,
    description,
    footer = null,
    headSelecter = null,
    onClick,
    onSwitchClick,
    onDelete,
    onAddTemp,
    onCheckedChange,
    onSetting,
    onAccreditDia,
    onDataInfo
  }: IProps<T>,
  _ref
) {
  // 功能权限管理Hook
  const { hasFuncPermission } = useFuncsStore()
  // 埋点
  const { applicationBuriedEvent } = useBuriedEvent()

  // 本地开关状态，用于控制在线/离线状态
  const [_checked, setChecked] = useState(checked)

  // 弹出层悬停状态，控制弹出层的显示/隐藏
  const [isPopoverHovered, setIsPopoverHovered] = useState(false)

  // 弹出层位置状态，动态计算弹出层的最佳显示位置
  const [popoverPosition, setPopoverPosition] = useState({ top: '21px', left: '0' })

  // 触发弹出层的DOM元素引用
  const triggerRef = useRef<HTMLDivElement>(null)

  // 弹出层DOM元素引用
  const popoverRef = useRef<HTMLDivElement>(null)

  // 路由导航Hook
  const navigate = useNavigate()

  // 国际化翻译Hook
  const { t } = useTranslation()

  /**
   * 监听窗口大小变化，重新计算弹出层位置
   * 当窗口大小改变时，如果弹出层正在显示，则重新计算其位置
   */
  useEffect(() => {
    const handleResize = () => {
      if (isPopoverHovered) {
        const position = calculatePopoverPosition()
        setPopoverPosition(position)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isPopoverHovered])

  /**
   * 计算弹出层最佳位置，避免超出视窗边界
   * 智能计算弹出层的显示位置，确保在视窗内可见
   *
   * @returns 包含top和left位置的对象
   */
  const calculatePopoverPosition = () => {
    if (!triggerRef.current) return { top: '21px', left: '0' }

    // 获取触发元素的位置信息
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const popoverWidth = 120 // 弹出层宽度（从CSS中获取）
    const popoverHeight = 100 // 弹出层高度（保守估算）
    const margin = 16 // 安全边距

    let top = '21px'
    let left = '0'

    // 计算水平位置 - 优先右侧，其次左侧，最后居中
    const spaceRight = viewportWidth - triggerRect.right - margin
    const spaceLeft = triggerRect.left - margin

    if (spaceRight >= popoverWidth) {
      // 右侧空间足够，默认显示在右侧
      left = '0'
    } else if (spaceLeft >= popoverWidth) {
      // 左侧空间足够，显示在左侧
      left = `-${popoverWidth - 20}px`
      // left = `-${popoverWidth / 2}px`
      // left = `0px`
    } else {
      // 左右空间都不够，居中显示
      const centerOffset = (popoverWidth - 20) / 2 // 20px 是触发按钮宽度
      left = `-${centerOffset}px`
    }

    // 计算垂直位置 - 优先下方，其次上方，最后选择空间较大的一侧
    const spaceBottom = viewportHeight - triggerRect.bottom - margin
    const spaceTop = triggerRect.top - margin

    if (spaceBottom >= popoverHeight) {
      // 下方空间足够，默认显示在下方
      top = '21px'
      console.log('显示在下方 - 空间充足')
    } else if (spaceTop >= popoverHeight) {
      // 上方空间足够，显示在上方
      top = `-${popoverHeight + 2 + 32}px`
      console.log('显示在上方 - 空间充足')
    } else {
      // 上下空间都不够，选择空间较大的一侧
      if (spaceBottom > spaceTop) {
        top = '21px'
        console.log('空间不足，选择下方 - 下方空间更大')
      } else {
        top = `-${popoverHeight + 2 + 32}px`
        console.log('空间不足，选择上方 - 上方空间更大')
      }
    }

    console.log('最终位置:', { top, left })
    return { top, left }
  }

  /**
   * 处理开关状态变化
   * 包含权限检查和状态更新逻辑
   *
   * @param bln - 新的开关状态（true为在线，false为离线）
   */
  const handleCheckedChange = async bln => {
    console.log(bln)

    // 权限检查：根据开关状态检查相应的权限
    if (bln) {
      // 上线操作需要上线权限
      if (!hasFuncPermission([Func.assistant.online])) {
        return
      }
    } else {
      // 下线操作需要下线权限
      if (!hasFuncPermission([Func.assistant.offline])) {
        return
      }
    }

    // 调用父组件的状态变化处理函数
    const res = await onCheckedChange(bln, data)
    if (res === false) return // 如果返回false，则不更新本地状态

    // 上线/下线埋点
    applicationBuriedEvent({
      event_name: bln ? '【上线】按钮' : '【下线】按钮',
      page: 'Agent页面',
      data_id: id,
      detail: JSON.stringify({ 操作: bln ? '上线Agent' : '下线Agent', AgentID: id, Agent名称: title })
    })
    // 更新本地开关状态
    setChecked(bln)
  }

  // 根据数据状态动态生成弹出层菜单列表
  const list =
    data?.status === 1
      ? [
          { name: '发布信息', type: 'toplineMessage' },
          { name: '使用数据', type: 'data' }
        ]
      : [{ name: '使用数据', type: 'data' }]

  /**
   * 处理弹出层菜单项点击事件
   * 根据菜单项类型执行相应的操作
   *
   * @param item - 菜单项对象，包含name和type属性
   */
  let listFun = item => {
    if (item.type === 'data') {
      // 跳转到应用编辑页面的看板模式
      onSetting('data')
    } else if (item.type === 'toplineMessage') {
      // 发布信息埋点
      applicationBuriedEvent({
        event_name: '【发布信息】按钮',
        page: 'Agent页面',
        data_id: id,
        detail: JSON.stringify({ 操作: '点击发布信息', AgentID: id, Agent名称: title })
      })
      // 跳转到发布信息页面
      onDataInfo()
    } else if (item.type === 'delete') {
      // 删除操作
      onDelete(data)
    }
  }
  /**
   * 渲染弹出层内容
   * 包含动态菜单项和删除选项
   *
   * @returns 弹出层JSX内容
   */
  const popoverContent = () => (
    <div
      className="flexCol m-0 p-0"
      onClick={e => {
        e.stopPropagation() // 阻止事件冒泡，避免触发卡片点击事件
      }}
    >
      {/* 动态渲染菜单项 */}
      {list.map((item, index) => (
        <span
          key={index + item.name}
          className={`cardComhidList cursonCase flexRowStart text-[#131921]`}
          onClick={e => {
            listFun(item)
          }}
        >
          {item.name}
        </span>
      ))}

      {/* 分隔线 */}
      <svg
        className="my-[4px]"
        width="104"
        height="1"
        viewBox="0 0 104 1"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <line x1="8" y1="0.5" x2="96" y2="0.5" stroke="#F4F5F8" />
      </svg>

      {/* 删除选项 */}
      <span
        key={'3+删除'}
        className={`cardComhidList cursonCase flexRowStart text-[#FA514B]`}
        onClick={e => {
          listFun({
            name: '删除',
            type: 'delete'
          })
        }}
      >
        删除
      </span>
    </div>
  )

  // 新建小卡片（sheet类型）- 用于侧边弹窗中的新建选项
  if (!id && type === 'sheet') {
    return (
      <Card
        className="group w-[320px] cursor-pointer border-dashed border-[#BEC6D6] bg-background-new transition hover:border-primary hover:shadow-none"
        onClick={onClick}
      >
        <CardHeader>
          <div className="flex justify-between pb-2">
            {/* 新建图标，悬停时变色 */}
            <PlusIcon className="transition-none group-hover:text-primary" />
          </div>
          <CardTitle className="">{title}</CardTitle>
        </CardHeader>
        <CardContent className="h-0 overflow-auto p-2 scrollbar-hide">
          <CardDescription className="cardDescri">{description}</CardDescription>
        </CardContent>
        <CardFooter className="flex h-10 justify-end">
          {/* 箭头图标，悬停时变色 */}
          <div className="cursor-pointer rounded">
            <GoIcon className="transition-none group-hover:text-primary" />
          </div>
        </CardFooter>
      </Card>
    )
  }

  // 新建卡片 - 用于主列表中的新建选项
  if (!id) {
    return (
      <Card
        className=" application_card group cursor-pointer border-dashed border-[#BEC6D6] transition hover:border-primary hover:shadow-none"
        onClick={onClick}
        style={{ backgroundColor: '#F5F7FB' }}
      >
        <CardHeader className="flexRow">
          <div className="flex justify-between">
            {/* 自定义的新建图标SVG - 展示文档和加号图标 */}
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect y="18.7017" width="42" height="54" rx="8" transform="rotate(-21.953 0 18.7017)" fill="#D6D9FF" />
              <rect x="17.571" y="8.89307" width="42" height="54" rx="8" fill="url(#paint0_linear_4810_820)" />
              <rect x="26.571" y="23.8931" width="24" height="4" rx="2" fill="white" />
              <rect x="26.571" y="33.8931" width="24" height="4" rx="2" fill="white" />
              <rect x="26.571" y="43.8931" width="12" height="4" rx="2" fill="white" />
              <circle cx="59" cy="52" r="11" fill="black" />
              <rect x="54" y="51" width="10" height="2" rx="1" fill="white" />
              <rect x="58" y="57" width="10" height="2" rx="1" transform="rotate(-90 58 57)" fill="white" />
              <defs>
                <linearGradient
                  id="paint0_linear_4810_820"
                  x1="17.571"
                  y1="35.8931"
                  x2="59.571"
                  y2="35.8931"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#3B74FB" />
                  <stop offset="0.505" stopColor="#6468F7" />
                  <stop offset="1" stopColor="#9659F2" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="application_card_cont h-[140px] overflow-auto scrollbar-hide">
          <CardDescription className="cardDescri">{description}</CardDescription>
        </CardContent>
        {/* 注释掉的底部箭头图标 */}
        {/* <CardFooter className="flex h-10 justify-end" style={{ background: '#F5F7FB' }}>
          <div className="cursor-pointer rounded">
            <GoIcon className="transition-none group-hover:text-primary" />
          </div>
        </CardFooter> */}
      </Card>
    )
  }

  // 侧边弹窗列表（sheet类型）- 用于侧边栏中的选项列表
  if (type === 'sheet') {
    return (
      <Card
        className="group relative w-[320px] cursor-pointer bg-[#F7F9FC] hover:bg-[#EDEFF6] hover:shadow-none"
        onClick={onClick}
      >
        <CardHeader className="pb-2">
          <CardTitle className="truncate-doubleline">
            <div className="flex items-center gap-2 pb-2">
              {/* 动态生成的图标背景 */}
              <TitleIconBg id={id}>
                <Icon />
              </TitleIconBg>
              <p className=" align-middle">{title}</p>
            </div>
            {/* <span></span> */}
          </CardTitle>
        </CardHeader>
        <CardContent className="mb-2 h-fit max-h-[60px] overflow-auto scrollbar-hide">
          <CardDescription className="cardDescri break-all">{description}</CardDescription>
        </CardContent>
        <CardFooter className=" block">{footer}</CardFooter>
      </Card>
    )
  }

  // 技能/助手组件 - 主要的卡片类型，包含完整的交互功能
  return (
    <Card
      className="application_card group cursor-pointer bg-background-Assistant hover:bg-background-hoverAssistant"
      onClick={() => {
        if (edit && onClick) {
          // 点击卡片埋点
          applicationBuriedEvent({
            event_name: '【点击Agent卡片】按钮',
            page: 'Agent页面',
            data_id: id,
            detail: JSON.stringify({ 操作: '点击Agent卡片', AgentID: id, Agent名称: title })
          })
          onClick()
        }
      }}
    >
      <CardHeader>
        <div className="flex justify-between pb-2">
          {/* 图标区域：优先显示logo，否则显示默认图标 */}
          {logo ? (
            <img className="card_icon_application" key={id} width={24} height={24} src={logo} />
          ) : (
            <TitleIconBg id={id}>
              {type === 'skill' ? (
                <SkillIcon className="card_icon_application" />
              ) : (
                <AssistantIcon className="card_icon_application" />
              )}
            </TitleIconBg>
          )}

          {/* 头部操作区域：包含选择器和开关 */}
          <div className="flex items-center gap-1">
            {headSelecter}
            {/* 在线/离线状态开关 */}
            <Switch
              style={{ background: 'red !important' }}
              checked={_checked}
              className="w-12"
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              texts={[t('skills.online'), t('skills.offline')]}
              onCheckedChange={b => edit && handleCheckedChange(b)}
              onClick={e => {
                e.stopPropagation() // 阻止事件冒泡
                onSwitchClick?.()
              }}
            ></Switch>
          </div>
        </div>
        {/* 卡片标题 */}
        <CardTitle className="domOverflowEllip cardTitle_span">{title}</CardTitle>
      </CardHeader>
      {/* <CardContent className="h-[120px] overflow-auto scrollbar-hide"> */}
      {/* 卡片内容区域 */}
      <CardContent className="h-[120px] scrollbar-hide">
        <CardDescription className="cardDescri domOverflowEllip_thr break-all" style={{ WebkitLineClamp: 5 }}>
          {description || t('application.contEmpty')}
        </CardDescription>
      </CardContent>

      {/* 卡片底部区域：包含用户信息和操作按钮 */}
      <CardFooter className="flex h-10 justify-between" style={{ marginTop: '20px' }}>
        {/* 用户信息区域 */}
        <div className="flex max-w-[calc(100%-100px)] items-center gap-1">
          {/* 注释掉的用户图标和创建者标签 */}
          {/* <UserIcon /> */}
          {/* <span style={{ minWidth: '74px' }} className="cardFouter_span_title text-sm text-muted-foreground">
            @{t('skills.createdBy')}
          </span> */}
          <span className="domOverflowEllip w-[100%] overflow-hidden text-ellipsis text-sm font-medium leading-none text-muted-foreground">
            @{user}
          </span>
        </div>
        {/* 编辑模式下的操作按钮区域 */}
        {edit && (
          <div className="hidden group-hover:flex">
            {/* 注释掉的授权按钮 */}
            {/* <div className=""> */}
            {/* 授权 */}
            {/* {
              <div
                className="cursor-pointer rounded hover:bg-[#EAEDF3]"
                onClick={e => {
                  e.stopPropagation()
                  onAccreditDia()
                }}
              >
                <AccreditIcon />
              </div>
            } */}

            {/* 注释掉的管理员添加模板按钮 */}
            {/* {isAdmin && type === 'skill' && (
              <div
                className="cursor-pointer rounded hover:bg-[#EAEDF3]"
                onClick={e => {
                  e.stopPropagation()
                  onAddTemp(data)
                }}
              >
                <AddToIcon />
              </div>
            )} */}

            {/* 更多操作按钮 - 触发弹出层菜单 */}
            <div
              ref={triggerRef}
              className="cardComPop cursonCase relative h-[20px] w-[20px] rounded pl-[2px] hover:bg-[#EAEDF3]"
              onMouseEnter={() => {
                const position = calculatePopoverPosition()
                setPopoverPosition(position)
                setIsPopoverHovered(true)
              }}
              onMouseLeave={() => setIsPopoverHovered(false)}
              onClick={e => {
                e.stopPropagation()
              }}
            >
              <SmallDashOutlined />
              {/* 动态定位的弹出层菜单 */}
              {isPopoverHovered && (
                <div
                  ref={popoverRef}
                  className="cardPopover"
                  style={{
                    top: popoverPosition.top,
                    left: popoverPosition.left
                  }}
                >
                  {popoverContent()}
                </div>
              )}
            </div>
            {/* 发布信息按钮 - 仅在已发布状态(status=1)时显示 */}
            {data?.status === 1 && (
              <Tooltip title="发布信息" className="cardComPopOpposition">
                <div
                  className="cursor-pointer rounded hover:bg-[#EAEDF3]"
                  onClick={e => {
                    e.stopPropagation()
                    // 发布信息埋点
                    applicationBuriedEvent({
                      event_name: '【发布信息】按钮',
                      page: 'Agent页面',
                      data_id: id,
                      detail: JSON.stringify({ 操作: '点击发布信息按钮', AgentID: id, Agent名称: title })
                    })
                    listFun({
                      name: '发布信息',
                      type: 'toplineMessage'
                    })
                  }}
                >
                  {/* 文档图标SVG */}
                  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M11.4544 3.00195H5.53035C4.66842 3.00195 4.02344 3.64889 4.02344 4.40137V15.6025C4.02344 16.357 4.67037 17.002 5.53035 17.002H14.4702C15.3321 17.002 15.9771 16.355 15.9771 15.6025V7.20215L11.4544 3.00195ZM12.8538 14.2031H7.03921V12.8037H12.9633V14.2031H12.8538ZM12.8538 11.4023H7.03921V10.0029H12.9633V11.4023H12.8538ZM10.7 7.95658V4.07888L14.7927 7.95658H10.7Z"
                      fill="#7587A5"
                    />
                  </svg>
                </div>
              </Tooltip>
            )}
            {/* 
            使用数据按钮的显隐逻辑：所有应用，无论是否上线过，都展示使用数据
            */}
            {/* 使用数据按钮 */}
            <Tooltip title="使用数据" className="cardComPopOpposition">
              <div
                className="ml-[6px] cursor-pointer rounded hover:bg-[#EAEDF3]"
                onClick={e => {
                  e.stopPropagation()
                  // 使用数据埋点
                  applicationBuriedEvent({
                    event_name: '【使用数据】按钮',
                    page: 'Agent页面',
                    data_id: id,
                    detail: JSON.stringify({ 操作: '点击使用数据按钮', AgentID: id, Agent名称: title })
                  })
                  listFun({
                    name: '使用数据',
                    type: 'data'
                  })
                }}
              >
                {/* 数据图表图标SVG */}
                <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M16.597 15.6875H16.1875V10.8149C16.1875 10.3559 15.8203 10 15.3677 10H13.7581C13.2974 10 12.9375 10.3648 12.9375 10.8149V15.6875H12.125V4.31819C12.125 3.86481 11.7578 3.5 11.3052 3.5H9.69481C9.23494 3.5 8.875 3.86644 8.875 4.31819V15.6875H8.0625V7.55925C8.0625 7.11156 7.69525 6.75 7.24269 6.75H5.63231C5.17244 6.75 4.8125 7.11237 4.8125 7.55925V15.6875H4.403C4.18362 15.6875 4 15.8695 4 16.0938C4 16.3115 4.18038 16.5 4.403 16.5H16.5962C16.8164 16.5 17 16.318 17 16.0938C17 15.876 16.8196 15.6875 16.597 15.6875Z"
                    fill="#7587A5"
                  />
                </svg>
              </div>
            </Tooltip>
            {/* 编辑按钮 - 设置图标 */}
            {
              <div
                className="ml-[6px] cursor-pointer rounded hover:bg-[#EAEDF3]"
                onClick={e => {
                  e.stopPropagation()
                  // 编辑按钮埋点
                  applicationBuriedEvent({
                    event_name: '【编辑】按钮',
                    page: 'Agent页面',
                    data_id: id,
                    detail: JSON.stringify({ 操作: '点击编辑按钮', AgentID: id, Agent名称: title })
                  })
                  onSetting(data)
                }}
              >
                {/* 设置/编辑图标SVG */}
                <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10.6229 12.1158C9.92287 12.2314 9.21037 12.008 8.70881 11.5173C8.20725 11.0267 7.98069 10.3267 8.09944 9.64234C8.24631 8.75484 8.97756 8.03765 9.88381 7.89077C10.5854 7.77202 11.301 7.99546 11.8041 8.48765C12.3072 8.9814 12.5338 9.68296 12.4119 10.3689C12.2604 11.258 11.5291 11.972 10.6229 12.1158ZM15.7557 10.7345L16.7869 10.1033C16.951 10.0033 17.0307 9.81265 16.9885 9.62827L16.4838 7.45796C16.4416 7.27359 16.2838 7.13609 16.0916 7.11577L14.8807 6.98452C14.7604 6.97202 14.651 6.91265 14.576 6.82046L14.3729 6.57046C14.2979 6.47827 14.2635 6.35952 14.2776 6.24234L14.4166 5.05952C14.4385 4.87202 14.3369 4.69077 14.1635 4.60796L12.1166 3.64234C11.9432 3.56109 11.7354 3.59546 11.5979 3.72827L10.7385 4.57202C10.6541 4.65484 10.5385 4.70327 10.4166 4.70327H10.0932C9.97287 4.70327 9.85725 4.6564 9.77131 4.57202L8.91194 3.72827C8.776 3.5939 8.56819 3.55952 8.39319 3.64234L6.34787 4.60796C6.17444 4.69077 6.07287 4.87046 6.09475 5.05952L6.23381 6.2439C6.24787 6.36109 6.2135 6.47827 6.1385 6.57202L5.93694 6.8189C5.86194 6.91109 5.75256 6.97046 5.63225 6.98296L4.42131 7.11577C4.22912 7.13609 4.07287 7.27359 4.02912 7.45796L3.52287 9.62671C3.48069 9.81109 3.56037 10.0017 3.72444 10.1017L4.75569 10.7345C4.85725 10.797 4.93069 10.897 4.95725 11.0126L5.02912 11.3236C5.05569 11.4392 5.03537 11.5595 4.97131 11.6595L4.32131 12.6673C4.21819 12.8267 4.23069 13.033 4.351 13.1814L5.76819 14.9205C5.8885 15.0689 6.09162 15.1251 6.27287 15.0626L7.42131 14.6673C7.53537 14.6283 7.66037 14.6345 7.76819 14.6861L8.06037 14.8236C8.16975 14.8751 8.25256 14.9658 8.29162 15.0783L8.69162 16.2033C8.75569 16.3814 8.92756 16.5017 9.11975 16.5017H11.3901C11.5838 16.5017 11.7557 16.3814 11.8182 16.2033L12.2182 15.0783C12.2572 14.9673 12.3416 14.8751 12.4494 14.8236L12.7416 14.6861C12.8494 14.6345 12.9744 14.6283 13.0885 14.6673L14.2369 15.0626C14.4197 15.1251 14.6213 15.0689 14.7416 14.9205L16.1588 13.1814C16.2791 13.033 16.2901 12.8267 16.1885 12.6673L15.5385 11.6595C15.4744 11.5595 15.4526 11.4392 15.4791 11.3236L15.551 11.0142C15.5807 10.897 15.6526 10.797 15.7557 10.7345Z"
                    fill="#7587A5"
                  />
                </svg>
              </div>
            }
            {/* 删除按钮 */}
            <div
              className="cardComPopOpposition ml-[6px] cursor-pointer rounded hover:bg-[#EAEDF3]"
              onClick={e => {
                e.stopPropagation()
                // 删除按钮埋点
                applicationBuriedEvent({
                  event_name: '【删除】按钮',
                  page: 'Agent页面',
                  data_id: id,
                  detail: JSON.stringify({ 操作: '点击删除按钮', AgentID: id, Agent名称: title })
                })
                onDelete(data)
              }}
            >
              {/* 垃圾桶删除图标SVG */}
              <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16.25 5.5H4.75C4.55109 5.5 4.36032 5.42098 4.21967 5.28033C4.07902 5.13968 4 4.94891 4 4.75C4 4.55109 4.07902 4.36032 4.21967 4.21967C4.36032 4.07902 4.55109 4 4.75 4H8.5C8.5 3.73478 8.60536 3.48043 8.79289 3.29289C8.98043 3.10536 9.23478 3 9.5 3H11.5C11.7652 3 12.0196 3.10536 12.2071 3.29289C12.3946 3.48043 12.5 3.73478 12.5 4H16.25C16.4489 4 16.6397 4.07902 16.7803 4.21967C16.921 4.36032 17 4.55109 17 4.75C17 4.94891 16.921 5.13968 16.7803 5.28033C16.6397 5.42098 16.4489 5.5 16.25 5.5ZM15.5 15C15.5 15.5304 15.2893 16.0391 14.9142 16.4142C14.5391 16.7893 14.0304 17 13.5 17H7.5C6.96957 17 6.46086 16.7893 6.08579 16.4142C5.71071 16.0391 5.5 15.5304 5.5 15V6.5H15.5V15ZM9.5 9.25C9.5 9.05109 9.42098 8.86032 9.28033 8.71967C9.13968 8.57902 8.94891 8.5 8.75 8.5C8.55109 8.5 8.36032 8.57902 8.21967 8.71967C8.07902 8.86032 8 9.05109 8 9.25V13.75C8 13.9489 8.07902 14.1397 8.21967 14.2803C8.36032 14.421 8.55109 14.5 8.75 14.5C8.94891 14.5 9.13968 14.421 9.28033 14.2803C9.42098 14.1397 9.5 13.9489 9.5 13.75V9.25ZM13 9.25C13 9.05109 12.921 8.86032 12.7803 8.71967C12.6397 8.57902 12.4489 8.5 12.25 8.5C12.0511 8.5 11.8603 8.57902 11.7197 8.71967C11.579 8.86032 11.5 9.05109 11.5 9.25V13.75C11.5 13.9489 11.579 14.1397 11.7197 14.2803C11.8603 14.421 12.0511 14.5 12.25 14.5C12.4489 14.5 12.6397 14.421 12.7803 14.2803C12.921 14.1397 13 13.9489 13 13.75V9.25Z"
                  fill="#7587A5"
                />
              </svg>
            </div>
          </div>
        )}
      </CardFooter>
    </Card>
  )
}

/**
 * 修复的forwardRef函数
 *
 * 用于解决TypeScript中forwardRef的类型推断问题
 *
 * @template T - ref的类型
 * @template P - props的类型
 * @param render - 渲染函数
 * @returns 包装后的组件
 */
function fixedForwardRef<T, P = {}>(
  render: (props: P, ref: React.Ref<T>) => React.ReactNode
): (props: P & React.RefAttributes<T>) => React.ReactNode {
  return React.forwardRef(render) as any
}

// 导出使用forwardRef包装的CardComponent
export default fixedForwardRef(CardComponent)
